import React, { useCallback, useContext, useEffect, useState } from 'react'

import { AuthContext } from 'features/auth/auth-context'
import { ExportButtonView } from 'features/projects/features/export/components/export-button-view/ExportButtonView'
import { ExportModalView } from 'features/projects/features/export/components/export-modal-view/ExportModalView'
import { useExportDatasets } from 'features/projects/features/export/hooks/use-export-datasets'
import { useExportService } from 'features/projects/features/export/service'
import {
  DEFAULT_EXPORT_FILTERS,
  ExportFilters,
  seedExportFilters,
} from 'features/projects/features/export/types'
import type { ProjectFilters } from 'features/projects/utils/project-filters'
import { Role } from 'types/hub-role'

const SUCCESS_AUTO_CLOSE_MS = 2200

/**
 * Account-level RBAC roles (the `user_role` axis from `GET /user`)
 * allowed to bulk-export: member and above. Guests are excluded. The
 * BE route remains authoritative; this gate is a UX nicety so users
 * below member don't see a button they shouldn't use.
 */
const BULK_EXPORT_ROLES: readonly Role[] = [Role.Member, Role.Admin, Role.Owner]

export type ExportDatasetsContainerProps = {
  /**
   * The homepage's applied dataset filters. When present, the modal
   * opens pre-seeded with them (visibility/archived/data types/usage)
   * so the Export and Filters buttons agree instead of living on
   * islands.
   */
  pageFilters?: ProjectFilters
}

/**
 * Dataset-export sub-feature entry point.
 *
 * Renders the homepage Export button + filter modal. The component
 * gates itself on the user's account-level RBAC role (`user_role` from
 * `GET /user`) being member-or-above.
 */
export const ExportDatasetsContainer: React.FC<
  ExportDatasetsContainerProps
> = ({ pageFilters }) => {
  const authContext = useContext(AuthContext)
  const userDetails = authContext?.userDetails
  const userRole = userDetails?.user_role
  const canBulkExport = userRole != null && BULK_EXPORT_ROLES.includes(userRole)

  const service = useExportService()
  const performExport = useCallback(
    (nextFilters: ExportFilters) => service.exportDatasets(nextFilters),
    [service],
  )
  const { status, filename, errorMessage, triggerExport, reset } =
    useExportDatasets(performExport)

  const [modalOpen, setModalOpen] = useState(false)
  const [filters, setFilters] = useState<ExportFilters>(DEFAULT_EXPORT_FILTERS)

  const handleOpen = useCallback(() => {
    reset()
    setFilters(
      pageFilters ? seedExportFilters(pageFilters) : DEFAULT_EXPORT_FILTERS,
    )
    setModalOpen(true)
  }, [reset, pageFilters])

  const handleClose = useCallback(() => {
    setModalOpen(false)
    reset()
  }, [reset])

  const handleApply = useCallback(() => {
    void triggerExport(filters)
  }, [triggerExport, filters])

  // Auto-close after the success affirmation has had time to read.
  useEffect(() => {
    if (status !== 'success') return undefined
    const t = setTimeout(handleClose, SUCCESS_AUTO_CLOSE_MS)
    return () => clearTimeout(t)
  }, [status, handleClose])

  if (!canBulkExport) return null

  return (
    <>
      <ExportButtonView
        isPreparing={status === 'preparing'}
        onClick={handleOpen}
      />
      <ExportModalView
        open={modalOpen}
        filters={filters}
        status={status}
        filename={filename}
        errorMessage={errorMessage}
        onChange={setFilters}
        onApply={handleApply}
        onClose={handleClose}
      />
    </>
  )
}
